import type { BerLength } from './types';
import type { KLV } from './klv';
import { SMPTE_UL_RANDOM_INDEX_PACK } from './mxfDictionary';
import { Parser } from './parser';
import { ArraySerializerTarget, Serializer } from './serializer';
import { MultiArrayBuffer } from './multiArrayBuffer';

export interface RandomIndexPackEntry {
  bodySid: number,
  byteOffset: number,
}

export function parseRandomIndexPack(klv: KLV): RandomIndexPackEntry[] {
  const entries : RandomIndexPackEntry[] = [];
  const source = new MultiArrayBuffer();
  source.push(klv.value);

  const parser = new Parser(source);
  while (parser.remain() > 4) {
    const bodySid = parser.readUInt32BE();
    const byteOffset = parser.readUInt64BE();
    const entry : RandomIndexPackEntry = { bodySid, byteOffset };
    entries.push(entry);
  }

  // The very last thing in a MXF file is the RandomIndexPack and the very last 4 bytes
  // in the RandomIndexPack is how big the RandomIndexPack is. The point of this is for
  // a demuxer to quickly be able to determine where the RandomIndexPacket starts. We
  // don't need this information right now.
  parser.readUInt32BE();

  if (parser.remain() !== 0) {
    throw new Error('Trailing data in RandomIndexPack');
  }
  return entries;
}

export function serializeRandomIndexPack(randomIndexPack: RandomIndexPackEntry[]): KLV {
  const count = randomIndexPack.length;
  const target = new ArraySerializerTarget();
  const writer = new Serializer(target);
  for (const entry of randomIndexPack) {
    writer.writeUInt32BE(entry.bodySid);
    writer.writeUInt64BE(entry.byteOffset);
  }
  const length : BerLength = {
    length: (count * 12) + 4,
    bytesUsed: 4,
  };
  const key = SMPTE_UL_RANDOM_INDEX_PACK;

  const totalLength = 16 + length.bytesUsed + length.length;
  writer.writeUInt32BE(totalLength);

  const value = target.getArray();

  return { key, length, value };
}

